import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { combineLatest, Observable, ReplaySubject, Subscription, Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { LanguageService } from '@fcom/ui-translate';
import { CmsPage, CmsPlaceholder, CmsTemplate, PageViewType } from '@fcom/core-api';
import { GlobalBookingTravelClass } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces';
import {
  entrySet,
  enumValues,
  equals,
  isEmptyObjectOrHasEmptyValues,
  isNotBlank,
  isPresent,
  unsubscribe,
} from '@fcom/core/utils';
import { DestinationSearchBlockOffer } from '@fcom/cms/interfaces';
import { finShare } from '@fcom/rx';
import { DestinationSearchService } from '@fcom/destination-search/services/destination-search.service';
import {
  DestinationSearchFeatureState,
  DestinationSearchItem,
  DestinationSearchItemType,
  DestinationSearchQueryParamNames,
  DestinationSearchQueryParams,
  DestinationSearchViewStyle,
  DestinationSearchFilters,
  DestinationSearchFilterStateMeta,
} from '@fcom/destination-search/interfaces';
import { findDestinationsForQueryParams, flattenDestinations } from '@fcom/destination-search/utils/flatten';

import {
  allFilterStates,
  defaultFilters,
  filterState,
  fromLocationCodeState,
  restrictToDestinationsState,
} from '../../../store';
import { DestinationSearchOffersService } from '../../../services/destination-search-offers.service';
import {
  removeRouteUpdate,
  setDestinationsBeforeFiltering,
  setDestinationSearchSelection,
} from '../../../store/actions/destination-search-filter.actions';
import { ORIGIN_SELECTOR_CONFIG } from '../../../constants';
import { filterDestinations } from '../../../utils/filters';

@Component({
  selector: 'fin-destination-search',
  templateUrl: './destination-search.component.html',
  standalone: false,
})
export class DestinationSearchPageComponent implements OnInit, OnDestroy {
  @Input()
  content: CmsPlaceholder;

  @Input()
  content$: Observable<CmsPlaceholder>;

  @Input()
  template: CmsTemplate;

  viewStyle: DestinationSearchViewStyle = DestinationSearchViewStyle.TILES;

  /*
   * All possible destinations in a nested format
   */
  allDestinations$: Observable<DestinationSearchItem[]>;
  subscription: Subscription = new Subscription();
  selectedOriginLocationCode$: Observable<string>;
  identifier: string;
  meta: DestinationSearchFilterStateMeta;
  initialDestinationSearchSelection$: ReplaySubject<DestinationSearchFilters> = new ReplaySubject(1);
  filteredDestinations$: Observable<DestinationSearchBlockOffer[]>;
  showFilters$: Observable<boolean>;
  isFilterApplied$: Observable<boolean> = of(false);
  static readonly ALLOWED_DESTINATIONS_COUNT = 6;
  isSearchBarPresent = false;
  destinationTeaserText: string;
  destinationSearchFilters$: Observable<DestinationSearchFilters>;
  clearFilters$: Subject<void> = new Subject<void>();

  constructor(
    private destinationSearchService: DestinationSearchService,
    private activatedRoute: ActivatedRoute,
    private destinationSearchOffersService: DestinationSearchOffersService,
    private store$: Store<DestinationSearchFeatureState>,
    private router: Router,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.identifier = this.content.contentId.toString() + this.content.modificationDate.toString();
    this.meta = { id: this.identifier };

    this.isFilterApplied$ = this.store$.pipe(
      filterState(this.meta.id),
      map(
        ({ selectedDestinations, maxPrice, showDirectFlightsOnly, roundTripPrices }) =>
          selectedDestinations?.length > 0 || maxPrice > 0 || showDirectFlightsOnly || roundTripPrices
      )
    );

    this.isSearchBarPresent = isPresent(this.content?.localSettings?.restrictToDestinations);

    const defaultOrigin$ = this.getDefaultOriginLocationCode();

    const restrictionsFromLocalSettings$ = defaultOrigin$.pipe(
      map((defaultOrigin) => this.getRestrictionParamsFromLocalSettings(this.content, defaultOrigin))
    );

    const restrictionParams$ = combineLatest([
      this.store$.pipe(
        restrictToDestinationsState(this.meta.id),
        distinctUntilChanged((prev, next) => equals(prev, next)),
        startWith([])
      ),
      restrictionsFromLocalSettings$,
    ]).pipe(
      map(([restrictToDestinations, restrictionsFromLocalSettings]) => ({
        ...restrictionsFromLocalSettings,
        destination:
          restrictToDestinations?.length > 0 ? restrictToDestinations : restrictionsFromLocalSettings.destination,
      }))
    );

    this.allDestinations$ = restrictionParams$.pipe(
      switchMap((restrictionParams) =>
        this.destinationSearchService.destinationSearch(restrictionParams).pipe(take(1), finShare())
      )
    );

    // Make initial selection on startup
    this.subscription.add(
      combineLatest([
        this.content$.pipe(map((content) => content?.localSettings)),
        this.store$.pipe(allFilterStates()).pipe(switchMap((states) => of(states[this.identifier]))),
        this.allDestinations$,
        this.activatedRoute.queryParams,
      ])
        .pipe(
          take(1),
          map(([localSettings, state, allDestinations, queryParams]): DestinationSearchFilters => {
            // TODO: Disable query parameters on campaign page where multiple destinations search components are used in the same page
            const disableQueryParamsAndState = !!localSettings?.restrictToDestinations?.destinationsByOrigin;
            const hasLocalSettingsSelections =
              localSettings?.selectedTravelClass || localSettings?.tripType || localSettings?.filteredDestinations;
            const supportedParams = Object.keys(queryParams).filter((key) =>
              enumValues(DestinationSearchQueryParams).includes(key as DestinationSearchQueryParams)
            );
            if (!disableQueryParamsAndState && supportedParams.length > 0) {
              return this.getQueryParamsSelection(allDestinations, state, queryParams);
            }

            if (!disableQueryParamsAndState && state) {
              return state;
            }

            if (hasLocalSettingsSelections) {
              return this.getLocalSettingsSelection(allDestinations, localSettings);
            }

            return defaultFilters;
          })
        )
        .subscribe((selection) => {
          this.store$.dispatch(setDestinationSearchSelection({ selection, meta: this.meta }));
          this.initialDestinationSearchSelection$.next(selection);
        })
    );

    // Track query param changes outside of this component
    this.subscription.add(
      combineLatest([this.allDestinations$, this.activatedRoute.queryParams.pipe(skip(1))])
        .pipe(
          filter(() => !this.router.getCurrentNavigation()?.extras?.state?.skipFilterSet),
          map(
            ([allDestinations, queryParams]): DestinationSearchFilters =>
              this.getQueryParamsSelection(allDestinations, undefined, queryParams)
          )
        )
        .subscribe((selection) => {
          this.store$.dispatch(
            setDestinationSearchSelection({ selection: { ...selection, skipRouteUpdate: true }, meta: this.meta })
          );
          this.initialDestinationSearchSelection$.next(selection);
        })
    );

    // Update query params when filters are updated after the initial render
    this.subscription.add(
      this.initialDestinationSearchSelection$
        .pipe(
          take(1),
          switchMap(() =>
            this.store$.pipe(
              filterState(this.meta.id),
              map((state) => {
                const { destinationsBeforeFiltering: _, ...rest } = state;
                return rest;
              }),
              distinctUntilChanged((prev, next) => equals(prev, next)),
              skip(1)
            )
          ),
          distinctUntilChanged(
            (prev, next) =>
              equals(prev.selectedDestinations, next.selectedDestinations) &&
              prev.tripType === next.tripType &&
              prev.travelClass === next.travelClass &&
              prev.maxPrice === next.maxPrice &&
              prev.showDirectFlightsOnly === next.showDirectFlightsOnly &&
              prev.roundTripPrices === next.roundTripPrices
          )
        )
        .subscribe((state: DestinationSearchFilters) => {
          if (!state.skipRouteUpdate) {
            this.updateQueryParamsFromSelectedDestinations(state);
          } else {
            this.store$.dispatch(removeRouteUpdate({ meta: this.meta }));
          }
        })
    );

    this.selectedOriginLocationCode$ = defaultOrigin$.pipe(
      switchMap((defaultOrigin) =>
        this.store$.pipe(
          fromLocationCodeState(this.meta.id),
          distinctUntilChanged((prev, next) => equals(prev, next)),
          startWith(defaultOrigin)
        )
      )
    );

    const destinationsWithOffers$ = combineLatest([this.selectedOriginLocationCode$, restrictionParams$]).pipe(
      distinctUntilChanged(),
      switchMap(([selectedOriginLocationCode, restrictionParams]) =>
        this.destinationSearchOffersService.getDestinationsWithOffers(
          restrictionParams,
          this.identifier,
          this.content,
          selectedOriginLocationCode
        )
      ),
      finShare()
    );

    this.destinationSearchFilters$ = this.store$.pipe(
      allFilterStates(),
      map((states) => states[this.identifier]),
      distinctUntilChanged(),
      finShare()
    );

    this.filteredDestinations$ = combineLatest([this.destinationSearchFilters$, destinationsWithOffers$]).pipe(
      map(([filters, destinations]) =>
        filterDestinations(destinations, filters.roundTripPrices, filters.maxPrice, filters.showDirectFlightsOnly)
      ),
      finShare()
    );

    this.showFilters$ = this.allDestinations$.pipe(
      distinctUntilChanged(),
      map(
        (destinations) =>
          flattenDestinations(destinations).filter((d) => d.type === DestinationSearchItemType.DESTINATION)?.length >
          DestinationSearchPageComponent.ALLOWED_DESTINATIONS_COUNT
      )
    );
    const headerItem = this.template?.header?.find(
      (header) => header?.viewTypeName === PageViewType.NO_GRID_FULL_WIDTH_PAGE_WITH_TITLE
    );
    this.destinationTeaserText = headerItem?.teaserText;

    this.subscription.add(
      destinationsWithOffers$.subscribe((destinations) => {
        this.store$.dispatch(
          setDestinationsBeforeFiltering({
            meta: this.meta,
            destinationsBeforeFiltering: destinations,
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  clearAllFilters(): void {
    this.clearFilters$.next();
  }

  private getSupportedQueryParamValues(queryParams: Params): DestinationSearchQueryParamNames {
    const paramKeys = enumValues(DestinationSearchQueryParams);
    return paramKeys.reduce((selections, key) => {
      const params: string | undefined = queryParams[key];
      return {
        ...selections,
        ...(isPresent(params) && { [key]: params.split(',').map((p) => decodeURI(p)) }), // Don't add a prop if missing from params
      };
    }, {});
  }

  private getDefaultOriginLocationCode(): Observable<string> {
    return combineLatest([this.languageService.countryCode, this.activatedRoute.queryParams]).pipe(
      map(([countryCode, queryParams]) => {
        const originFromQuery = queryParams.origin?.toUpperCase();

        const customOrigins = Object.keys(
          this.content?.localSettings?.restrictToDestinations?.destinationsByOrigin || {}
        );

        if (customOrigins.length) {
          return originFromQuery && customOrigins.includes(originFromQuery) ? originFromQuery : customOrigins[0];
        }

        if (ORIGIN_SELECTOR_CONFIG[countryCode]?.length) {
          return originFromQuery && ORIGIN_SELECTOR_CONFIG[countryCode].includes(originFromQuery)
            ? originFromQuery
            : ORIGIN_SELECTOR_CONFIG[countryCode][0];
        }

        const cmsPageNode = this.template?.header?.[0];
        return (cmsPageNode as CmsPage)?.defaultOriginLocationCode ?? 'HEL';
      })
    );
  }

  private getQueryParamsSelection(
    destinations: DestinationSearchItem[],
    state: DestinationSearchFilters,
    params: Params
  ): DestinationSearchFilters {
    const queryParams = this.getSupportedQueryParamValues(params);
    const prefillTravelClass = GlobalBookingTravelClass[queryParams.cabin?.[0]?.toUpperCase()] ?? state?.travelClass;
    const prefillTripType = TripType[queryParams.type?.[0]?.toUpperCase()] ?? state?.tripType;
    const prefillSelectedDestinations = findDestinationsForQueryParams(destinations, {
      continent: queryParams.continent,
      country: queryParams.country,
      destination: queryParams.destination,
    });

    return {
      ...defaultFilters,
      ...(prefillTripType && { tripType: prefillTripType }),
      ...(prefillTravelClass && { travelClass: prefillTravelClass }),
      ...(prefillSelectedDestinations && { selectedDestinations: prefillSelectedDestinations }),
      ...(state?.tripDates && { tripDates: state?.tripDates }),
      ...(queryParams.maxPrice && { maxPrice: parseInt(queryParams.maxPrice[0], 10) }),
      ...(queryParams.directFlights && { showDirectFlightsOnly: queryParams.directFlights[0] === 'true' }),
      ...(queryParams.roundTripPrices && { roundTripPrices: queryParams.roundTripPrices[0] === 'true' }),
    };
  }

  private getLocalSettingsSelection(
    destinations: DestinationSearchItem[],
    localSettings: any
  ): DestinationSearchFilters {
    const prefillTravelClass = localSettings.selectedTravelClass
      ? GlobalBookingTravelClass[localSettings.selectedTravelClass.toUpperCase()]
      : undefined;
    const prefillTripType = localSettings.selectedTripType
      ? TripType[localSettings.selectedTripType.toUpperCase()]
      : undefined;
    const prefillSelectedDestinations = localSettings.filteredDestinations
      ? findDestinationsForQueryParams(destinations, localSettings.filteredDestinations)
      : undefined;

    return {
      ...defaultFilters,
      ...(prefillTripType && { tripType: prefillTripType }),
      ...(prefillTravelClass && { travelClass: prefillTravelClass }),
      ...(prefillSelectedDestinations && { selectedDestinations: prefillSelectedDestinations }),
    };
  }

  private getRestrictionParamsFromLocalSettings(
    content: CmsPlaceholder,
    defaultOrigin: string
  ): DestinationSearchQueryParamNames {
    const restrictToDestinationsRequest = content?.localSettings?.restrictToDestinations;
    const requestIsInSupportedFormat =
      restrictToDestinationsRequest &&
      !isEmptyObjectOrHasEmptyValues(restrictToDestinationsRequest) &&
      Object.keys(restrictToDestinationsRequest).every((key) => key.toUpperCase() in DestinationSearchItemType) &&
      (restrictToDestinationsRequest.continent ? Array.isArray(restrictToDestinationsRequest.continent) : true) &&
      (restrictToDestinationsRequest.destination ? Array.isArray(restrictToDestinationsRequest.destination) : true);
    return requestIsInSupportedFormat
      ? {
          continent: restrictToDestinationsRequest.continent,
          country: restrictToDestinationsRequest.country,
          destination:
            restrictToDestinationsRequest.destinationsByOrigin?.[defaultOrigin] ??
            restrictToDestinationsRequest.destination,
        }
      : {};
  }

  private updateQueryParamsFromSelectedDestinations(state: DestinationSearchFilters): void {
    const destinationsAsQueryParams = state.selectedDestinations?.reduce(
      (params: DestinationSearchQueryParamNames, destination) => {
        switch (destination.type) {
          case DestinationSearchItemType.DESTINATION:
            return {
              ...params,
              destination: [...params.destination, destination.locationCode],
            };
          case DestinationSearchItemType.COUNTRY:
            return {
              ...params,
              country: [...params.country, destination.countryCode],
            };
          case DestinationSearchItemType.CONTINENT:
            return {
              ...params,
              continent: [...params.continent, destination.titleMaster],
            };
          default:
            return params;
        }
      },
      {
        destination: [],
        country: [],
        continent: [],
      }
    );

    const destinationQueryParams: DestinationSearchQueryParamNames = entrySet(destinationsAsQueryParams).reduce(
      (allParams, param: { key: DestinationSearchQueryParams; value: string[] }) => {
        const paramString = param.value.join(',');
        return {
          ...allParams,
          // only add params for the types of destinations that are selected
          ...(isNotBlank(paramString) && { [param.key]: paramString }),
        };
      },

      {}
    );

    const queryParams: Params = {
      ...destinationQueryParams,
      type: state.tripType.toLowerCase(),
      cabin: state.travelClass.toLowerCase(),
      ...(state.maxPrice && { maxPrice: state.maxPrice }),
      ...(state.showDirectFlightsOnly && { directFlights: 'true' }),
      ...(state.roundTripPrices && { roundTripPrices: 'true' }),
    };

    if (!equals(queryParams, this.activatedRoute.snapshot.queryParams)) {
      this.router.navigate([], {
        queryParams,
        replaceUrl: true,
        state: { skipFilterSet: true },
      });
    }
  }
}
